import type { Agreements } from '../filterValues';
import {
  SEARCH_PARAMETERS_SCHEMA,
  SearchParameterFieldKind,
  schemaByName,
  type SearchParameterValues,
} from '../schema';
import type { SearchParameterSchemaRow } from '../schema/rows';

export type SearchQuery = Record<string, string | undefined>;

const AGREEMENT_KEY_MAP: Record<string, keyof Agreements> = {
  pa: 'premiumaccess',
  ed: 'editorialsubscription',
  ea: 'easyaccess',
  rf: 'royaltyfreesubscription',
};

type DeserializeStrategy = (
  field: SearchParameterSchemaRow,
  raw: string | undefined
) => unknown;

export class SearchParameterDeserializer {
  readonly #schemaMap: ReadonlyMap<string, SearchParameterSchemaRow>;

  constructor(
    schema: readonly SearchParameterSchemaRow[] = SEARCH_PARAMETERS_SCHEMA
  ) {
    this.#schemaMap = schemaByName(schema);
  }

  deserialize = (query: SearchQuery): SearchParameterValues => {
    const out: Record<string, unknown> = {};

    for (const [name, raw] of Object.entries(query)) {
      const field = this.#schemaMap.get(name);
      if (field) {
        const value = this.#strategies[field.kind](field, raw);
        if (value !== undefined) {
          out[name] = value;
        }
      }
    }

    return out as SearchParameterValues;
  };

  #deserializeString: DeserializeStrategy = (field, raw) => {
    if (!raw) return undefined;
    if ('validate' in field && field.validate?.(raw) === false)
      return undefined;
    return raw;
  };

  #deserializeNumber: DeserializeStrategy = (_field, raw) => {
    if (!raw) return undefined;
    const num = Number(raw);
    return Number.isNaN(num) ? undefined : num;
  };

  // An unrecognised value is not a value. It leaves the parameter unstated,
  // rather than standing in the row's `defaultValue` — storing that would say
  // the visitor asked for the default when what they asked for was nonsense,
  // and nothing downstream could tell the two apart. The getter supplies the
  // default on read, so the search still runs under it either way.
  #deserializeEnum: DeserializeStrategy = (field, raw) =>
    field.kind === SearchParameterFieldKind.ENUM
      ? this.#matchEnum(field.enum, raw)
      : undefined;

  // An all-invalid list leaves the parameter unstated rather than stated as
  // an empty one. `?viewpoint=nonsense` is not a choice of no viewpoints, and
  // storing `[]` would make `states()` answer that the visitor picked
  // something — which is what preference fill reads to decide whether to
  // restore their stored choice.
  #deserializeEnumArray: DeserializeStrategy = (field, raw) => {
    if (field.kind !== SearchParameterFieldKind.ENUM_ARRAY) return undefined;
    if (!raw) return undefined;
    const members = this.#splitStringList(raw)
      .map((v) => this.#matchEnum(field.enum, v))
      .filter((v): v is NonNullable<typeof v> => Boolean(v));
    return members.length ? members : undefined;
  };

  #deserializeStringArray: DeserializeStrategy = (_field, raw) => {
    if (!raw) return undefined;
    const members = this.#splitStringList(raw);
    return members.length ? members : undefined;
  };

  #deserializeAgreements: DeserializeStrategy = (_field, raw) =>
    this.#parseAgreements(raw);

  #matchEnum<T extends Record<string, string>>(
    enumObj: T,
    value?: string
  ): T[keyof T] | undefined {
    if (!value) return undefined;
    const values = Object.values(enumObj);
    const normalized = value.toLowerCase();
    const exact = values.find((v) => v.toLowerCase() === normalized);

    return (exact ?? this.#matchEnumCombination(values, normalized)) as
      | T[keyof T]
      | undefined;
  }

  /**
   * The value naming the same set as `value`, for an enum that holds
   * combinations — `creative,editorial`, `image,film`, `rf,rm`.
   *
   * A combination is one value written with a comma in it, so a url can spell
   * the same one more than one way: in the other order, with a member twice,
   * or alongside a member the enum does not know. None of those is a choice
   * the visitor made, and an exact comparison reads all three as nonsense and
   * leaves the parameter unstated — for a url that did name a value the enum
   * holds.
   *
   * Matched against the enum's own values rather than a per-row rule: if
   * `creative,editorial` is declared, that *is* how this enum spells that set,
   * so nothing has to say so a second time.
   *
   * Members the enum does not hold as values of their own are dropped, so
   * `rf,bogus` names `rf`. An enum without a comma in any value cannot reach a
   * new answer here — a lone value splits to itself, and the set it makes
   * matches only what the exact comparison already found.
   */
  #matchEnumCombination(
    values: readonly string[],
    normalized: string
  ): string | undefined {
    const members = new Set(
      this.#splitStringList(normalized).filter((member) =>
        values.some((v) => v.toLowerCase() === member)
      )
    );
    if (!members.size) return undefined;

    return values.find((v) => {
      const declared = v.toLowerCase().split(',');

      return (
        declared.length === members.size &&
        declared.every((member) => members.has(member))
      );
    });
  }

  #splitStringList(value?: string): string[] {
    if (!value) return [];
    return value
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  #parseAgreementPair(pair: string): Partial<Agreements> | undefined {
    const [key, value] = pair.split(':');
    const agreementKey = key ? AGREEMENT_KEY_MAP[key] : undefined;
    if (!agreementKey || !value) return undefined;
    return { [agreementKey]: pair };
  }

  #parseAgreements(value?: string): Agreements | undefined {
    if (!value) return undefined;
    const agreements = value
      .split(',')
      .map((pair) => this.#parseAgreementPair(pair))
      .filter(Boolean)
      .reduce<Agreements>((acc, part) => Object.assign(acc, part), {});
    return Object.keys(agreements).length ? agreements : undefined;
  }

  readonly #strategies: Record<SearchParameterFieldKind, DeserializeStrategy> =
    {
      [SearchParameterFieldKind.STRING]: this.#deserializeString,
      [SearchParameterFieldKind.ENUM]: this.#deserializeEnum,
      [SearchParameterFieldKind.ENUM_ARRAY]: this.#deserializeEnumArray,
      [SearchParameterFieldKind.STRING_ARRAY]: this.#deserializeStringArray,
      [SearchParameterFieldKind.AGREEMENTS]: this.#deserializeAgreements,
      [SearchParameterFieldKind.NUMBER]: this.#deserializeNumber,
    };
}
